import React, { useEffect, useMemo, useRef, useState } from 'react';

const isCoordinate = value => (
    value !== null && value !== undefined && value !== '' && Number.isFinite(Number(value))
);

const hasCoordinates = ({ lat, lng }) => isCoordinate(lat) && isCoordinate(lng);

const MastersMap = ({ masters = [], isMapApiLoaded }) => {
    const ref = useRef(null);
    const markersRef = useRef([]);
    const infoWindowRef = useRef(null);
    const [map, setMap] = useState(null);

    const mastersWithCoordinates = useMemo(
        () => masters.filter(hasCoordinates),
        [masters]
    );

    useEffect(() => {
        if (!isMapApiLoaded || !window.google || !ref.current || map || mastersWithCoordinates.length === 0) {
            return;
        }

        const firstMaster = mastersWithCoordinates[0];
        setMap(new window.google.maps.Map(ref.current, {
            center: { lat: Number(firstMaster.lat), lng: Number(firstMaster.lng) },
            zoom: 6,
            maxZoom: 18,
            gestureHandling: 'cooperative',
            disableDefaultUI: true,
            fullscreenControl: true,
            zoomControl: true,
            keyboardShortcuts: true,
        }));
    }, [isMapApiLoaded, map, mastersWithCoordinates]);

    useEffect(() => {
        if (!map || !isMapApiLoaded || !window.google) return;

        markersRef.current.forEach(marker => marker.setMap(null));
        markersRef.current = [];

        if (!infoWindowRef.current) {
            infoWindowRef.current = new window.google.maps.InfoWindow({ maxWidth: 280 });
        }

        const bounds = new window.google.maps.LatLngBounds();

        mastersWithCoordinates.forEach(master => {
            const position = { lat: Number(master.lat), lng: Number(master.lng) };
            const marker = new window.google.maps.Marker({
                position,
                map,
                title: `${master.firstName || ''} ${master.lastName || ''}`.trim(),
                icon: {
                    path: window.google.maps.SymbolPath.CIRCLE,
                    scale: 9,
                    fillColor: '#0101ff',
                    strokeColor: '#0101ff',
                    fillOpacity: 1,
                    strokeWeight: 9,
                    strokeOpacity: 0.15,
                },
            });

            marker.addListener('click', () => {
                const content = document.createElement('div');
                const name = document.createElement('strong');
                const details = document.createElement('div');

                name.textContent = `${master.firstName || ''} ${master.lastName || ''}`.trim();
                details.textContent = [master.tagLine, master.cityName, master.country]
                    .filter(Boolean)
                    .join(' · ');
                details.style.marginTop = '4px';
                content.append(name, details);

                infoWindowRef.current.setContent(content);
                infoWindowRef.current.open({ map, anchor: marker });
            });

            markersRef.current.push(marker);
            bounds.extend(position);
        });

        if (mastersWithCoordinates.length === 1) {
            map.setCenter(bounds.getCenter());
            map.setZoom(11);
        } else if (mastersWithCoordinates.length > 1) {
            map.fitBounds(bounds, 45);
        }
    }, [map, isMapApiLoaded, mastersWithCoordinates]);

    if (!isMapApiLoaded) {
        return <div className="notification notice">Loading map...</div>;
    }

    if (mastersWithCoordinates.length === 0) {
        return <div className="notification notice">No masters with a configured location.</div>;
    }

    return (
        <div
            ref={ref}
            aria-label="Masters map"
            style={{ width: '100%', height: '430px', marginBottom: '35px', borderRadius: '4px' }}
        />
    );
};

export default MastersMap;
